import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { format } from "date-fns";
import { 
  Building2, 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  ArrowUpRight, 
  ArrowDownRight,
  Receipt,
  Trash2,
  Package,
  FileText,
  Download,
  Calendar as CalendarIcon,
  Filter,
  X,
  Printer,
  User,
  Pencil,
  Truck,
  Fuel,
  CreditCard,
  FileCheck,
  CircleDollarSign,
  Settings,
  Save,
  LogOut,
  LogIn,
  FileSpreadsheet,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { InvoiceView } from "@/components/invoice";
import { CoalLedgerView } from "@/components/ledger";
import { FuelVendorSummary } from "@/components/fuel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== AUTH COMPONENTS ====================

// Login Page Component
const LoginPage = ({ onLogin }) => {
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur">
        <CardHeader className="text-center space-y-4">
          <div className="w-20 h-20 bg-primary rounded-xl mx-auto flex items-center justify-center">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Dual Ledger</CardTitle>
          <p className="text-slate-400 text-sm">Multi-Company Financial Management</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <Button 
            onClick={handleGoogleLogin}
            className="w-full h-12 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-lg flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>
          <p className="text-xs text-center text-slate-500">
            Secure login powered by Google
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Auth Callback Handler
const AuthCallback = ({ onAuthSuccess }) => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        window.location.href = '/';
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId
        }, { withCredentials: true });

        // Clear the hash
        window.history.replaceState(null, '', window.location.pathname);
        
        onAuthSuccess(response.data);
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Login failed. Please try again.');
        window.location.href = '/';
      }
    };

    processAuth();
  }, [onAuthSuccess]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-white">Logging you in...</p>
      </div>
    </div>
  );
};

// A1 Transport Expense Categories
const TRANSPORT_EXPENSE_CATEGORIES = [
  { id: "fuel", name: "Tell (Fuel)", icon: Fuel },
  { id: "driver_salary", name: "Driver Salary", icon: User },
  { id: "codriver_salary", name: "Co-Driver Salary", icon: User },
  { id: "kisti", name: "Kisti (EMI)", icon: CreditCard },
  { id: "paper", name: "Paper (RTO/Docs)", icon: FileCheck },
  { id: "toll", name: "Toll", icon: CircleDollarSign }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Balance Card Component
const BalanceCard = ({ title, amount, icon: Icon, variant = "default", overline }) => {
  const isPositive = variant === "credit";
  const isNegative = variant === "debit";
  
  return (
    <Card 
      className="balance-card border border-slate-200 shadow-sm hover:border-primary/50 transition-colors duration-300"
      data-testid={`balance-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <CardContent className="p-6">
        {overline && (
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-500 mb-2">
            {overline}
          </p>
        )}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <p className={`font-mono text-2xl font-bold tracking-tight animate-number ${
              isPositive ? 'text-accent-success' : isNegative ? 'text-accent-danger' : 'text-slate-900'
            }`}>
              {formatCurrency(amount)}
            </p>
          </div>
          <div className={`p-3 rounded-sm ${
            isPositive ? 'bg-green-50' : isNegative ? 'bg-red-50' : 'bg-slate-100'
          }`}>
            <Icon className={`w-5 h-5 ${
              isPositive ? 'text-accent-success' : isNegative ? 'text-accent-danger' : 'text-slate-600'
            }`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Transaction Form Component
const TransactionForm = ({ company, categories, onSuccess, onClose }) => {
  const [type, setType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [transactionDate, setTransactionDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  
  // A1 Coal specific fields
  const [perUnitCost, setPerUnitCost] = useState("");
  const [perTransportCost, setPerTransportCost] = useState("");
  const [paid, setPaid] = useState("");
  const [partyName, setPartyName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [totalMaterial, setTotalMaterial] = useState("");
  const [materialType, setMaterialType] = useState("coal"); // Coal or Sand
  
  // A1 Transport specific fields
  const [expenseCategory, setExpenseCategory] = useState("");
  
  // Payment mode for A1 Coal paisa mila
  const [paymentMode, setPaymentMode] = useState("cash");
  
  // Fuel vendor for A1 Transport fuel expenses
  const [fuelVendor, setFuelVendor] = useState("");
  const [fuelVendorsList, setFuelVendorsList] = useState([]);
  
  // Party names list for dropdown
  const [partyNamesList, setPartyNamesList] = useState([]);
  // Vehicle list for A1 Transport
  const [vehiclesList, setVehiclesList] = useState([]);

  // Material options for A1 Coal
  const MATERIAL_OPTIONS = [
    { id: "coal", name: "Coal (कोयला)" },
    { id: "sand", name: "Sand (बालू)" }
  ];

  const isCoalCompany = company === "A1 Coal";
  const isTransportCompany = company === "A1 Transport";
  const isPaymentReceived = isCoalCompany && type === "payment";
  const isTransportIncome = isTransportCompany && type === "credit";
  const isTransportExpense = isTransportCompany && type === "debit";
  
  // Fetch party names and vehicles for A1 Coal and A1 Transport (shared data)
  useEffect(() => {
    if (isCoalCompany || isTransportCompany) {
      // Fetch party names - shared between A1 Coal and A1 Transport
      axios.get(`${API}/party-names?company=${company}`)
        .then(res => setPartyNamesList(res.data))
        .catch(() => {}); // Silent fail
      
      // Fetch vehicles - shared between A1 Coal and A1 Transport
      axios.get(`${API}/vehicles?company=${company}`)
        .then(res => setVehiclesList(res.data))
        .catch(() => {}); // Silent fail
      
      // Fetch fuel vendors for A1 Transport
      if (isTransportCompany) {
        axios.get(`${API}/fuel-vendors`)
          .then(res => setFuelVendorsList(res.data))
          .catch(() => {});
      }
    }
  }, [isCoalCompany, isTransportCompany, company]);
  
  // Calculate sub total and due for A1 Coal
  // Sub Total = (Per Cost * Total Material) + (Per Transport Cost * Total Material)
  const materialCost = perUnitCost && totalMaterial ? parseFloat(perUnitCost) * parseFloat(totalMaterial) : 0;
  const transportCost = perTransportCost && totalMaterial ? parseFloat(perTransportCost) * parseFloat(totalMaterial) : 0;
  const coalSubTotal = materialCost + transportCost;
  const coalDue = coalSubTotal - (paid ? parseFloat(paid) : 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For A1 Coal credit (माल दिया), use coalSubTotal
    // For A1 Coal payment (पैसा मिला), use direct amount
    let finalAmount;
    if (isCoalCompany && !isPaymentReceived && coalSubTotal > 0) {
      finalAmount = coalSubTotal;
    } else {
      finalAmount = parseFloat(amount);
    }
    
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please enter valid amount");
      return;
    }

    // Validate A1 Transport fields
    if (isTransportCompany) {
      if (!vehicleNumber) {
        toast.error("Please select or enter vehicle number");
        return;
      }
      if (isTransportExpense && !expenseCategory) {
        toast.error("Please select expense category");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        company: company,
        transaction_type: type,
        amount: finalAmount,
        description: description || "",
        category: null,
        transaction_date: transactionDate ? format(transactionDate, 'yyyy-MM-dd') : null
      };
      
      // Add A1 Coal fields for माल दिया (credit)
      if (isCoalCompany && !isPaymentReceived) {
        payload.party_name = partyName || null;
        payload.vehicle_number = vehicleNumber || null;
        payload.category = materialType || null; // Material type (coal/sand)
        payload.per_unit_cost = perUnitCost ? parseFloat(perUnitCost) : null;
        payload.transport_cost = perTransportCost ? parseFloat(perTransportCost) : null;
        payload.net_unit = totalMaterial ? parseFloat(totalMaterial) : null;
        payload.sub_total = coalSubTotal || null;
        payload.paid = paid ? parseFloat(paid) : null;
        payload.due = coalDue || null;
      }
      
      // Add पैसा मिला (payment received) for A1 Coal
      if (isPaymentReceived) {
        payload.transaction_type = "debit"; // Payment received reduces what's owed
        payload.party_name = partyName || null;
        payload.description = description || `पैसा मिला (${paymentMode === 'cash' ? 'Cash' : 'Account'})`;
        payload.category = "payment_received";
        payload.payment_mode = paymentMode;
      }

      // Add A1 Transport Income (कमाई)
      if (isTransportIncome) {
        payload.vehicle_number = vehicleNumber;
        payload.party_name = partyName && partyName !== "__new__" && partyName !== "none" ? partyName : null;
        payload.description = description || "कमाई";
        payload.category = "income";
      }

      // Add A1 Transport Expense (खर्चा)
      if (isTransportExpense) {
        payload.vehicle_number = vehicleNumber;
        payload.category = expenseCategory;
        payload.description = description || TRANSPORT_EXPENSE_CATEGORIES.find(c => c.id === expenseCategory)?.name || "खर्चा";
        // Add fuel vendor if it's a fuel expense
        if (expenseCategory === "fuel" || expenseCategory === "tell") {
          payload.fuel_vendor = fuelVendor && fuelVendor !== "__new__" ? fuelVendor : null;
        }
      }

      await axios.post(`${API}/transactions`, payload);
      toast.success(`${isPaymentReceived ? 'पैसा मिला' : isTransportIncome ? 'कमाई' : isTransportExpense ? 'खर्चा' : type === 'credit' ? 'Credit' : 'Debit'} entry added successfully`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Failed to add transaction");
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Transaction Type</Label>
        <Tabs value={type} onValueChange={setType} className="w-full">
          <TabsList className={`grid w-full ${isCoalCompany || isTransportCompany ? 'grid-cols-2' : 'grid-cols-2'}`}>
            <TabsTrigger 
              value="credit" 
              data-testid="transaction-type-credit"
              className={`data-[state=active]:bg-green-50 data-[state=active]:text-accent-success`}
            >
              <ArrowUpRight className="w-4 h-4 mr-2" />
              {isCoalCompany ? 'माल दिया' : isTransportCompany ? 'कमाई (Income)' : 'Credit'}
            </TabsTrigger>
            {isCoalCompany ? (
              <TabsTrigger 
                value="payment" 
                data-testid="transaction-type-payment"
                className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600"
              >
                <ArrowDownRight className="w-4 h-4 mr-2" />
                पैसा मिला
              </TabsTrigger>
            ) : (
              <TabsTrigger 
                value="debit" 
                data-testid="transaction-type-debit"
                className="data-[state=active]:bg-red-50 data-[state=active]:text-accent-danger"
              >
                <ArrowDownRight className="w-4 h-4 mr-2" />
                {isTransportCompany ? 'खर्चा (Expense)' : 'Debit'}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Transaction Date</Label>
        <div className="flex gap-2">
          <Input
            type="date"
            value={transactionDate ? format(transactionDate, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              if (e.target.value) {
                setTransactionDate(new Date(e.target.value));
              } else {
                setTransactionDate(new Date());
              }
            }}
            className="flex-1 rounded-sm font-mono"
            data-testid="transaction-date-input"
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-sm shrink-0"
                data-testid="transaction-date-calendar"
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={transactionDate}
                onSelect={(date) => setTransactionDate(date || new Date())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* A1 Coal Material Fields - माल दिया */}
      {isCoalCompany && !isPaymentReceived && (
        <div className="p-3 bg-amber-50 rounded-sm border border-amber-200">
          <p className="text-xs font-semibold text-amber-700 flex items-center gap-2 mb-3">
            <Package className="w-4 h-4" />
            माल दिया - MATERIAL SUPPLIED
          </p>
          
          <div className="space-y-3">
            {/* Party Name Dropdown + Gari No */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Party Name *</Label>
                {partyNamesList.length > 0 ? (
                  <Select value={partyName} onValueChange={setPartyName}>
                    <SelectTrigger className="rounded-sm text-sm" data-testid="coal-party-select">
                      <SelectValue placeholder="Party चुनें" />
                    </SelectTrigger>
                    <SelectContent>
                      {partyNamesList.map(name => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))}
                      <SelectItem value="__new__">+ नया Party जोड़ें</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Party का नाम"
                    value={partyName}
                    onChange={(e) => setPartyName(e.target.value)}
                    className="rounded-sm text-sm"
                    data-testid="coal-party-name-input"
                  />
                )}
                {partyName === "__new__" && (
                  <Input
                    placeholder="नया Party का नाम"
                    onChange={(e) => setPartyName(e.target.value)}
                    className="rounded-sm text-sm mt-2"
                    data-testid="coal-new-party-input"
                  />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Gari No.</Label>
                {vehiclesList.length > 0 ? (
                  <Select value={vehicleNumber} onValueChange={setVehicleNumber}>
                    <SelectTrigger className="rounded-sm text-sm" data-testid="coal-vehicle-select">
                      <SelectValue placeholder="Gari No. चुनें" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehiclesList.map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                      <SelectItem value="__new__">+ नई गाड़ी जोड़ें</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="MH 12 AB 1234"
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                    className="rounded-sm text-sm"
                    data-testid="coal-vehicle-input"
                  />
                )}
                {vehicleNumber === "__new__" && (
                  <Input
                    placeholder="नई गाड़ी का नंबर"
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                    className="rounded-sm text-sm mt-2"
                    data-testid="coal-new-vehicle-input"
                  />
                )}
              </div>
            </div>
            
            {/* Material Type Selection */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Material Type *</Label>
              <Select value={materialType} onValueChange={setMaterialType}>
                <SelectTrigger className="rounded-sm text-sm" data-testid="coal-material-select">
                  <SelectValue placeholder="Material चुनें" />
                </SelectTrigger>
                <SelectContent>
                  {MATERIAL_OPTIONS.map(mat => (
                    <SelectItem key={mat.id} value={mat.id}>{mat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Per Cost + Per Transport Cost */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Per Cost (₹/unit)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={perUnitCost}
                  onChange={(e) => setPerUnitCost(e.target.value)}
                  className="rounded-sm text-sm font-mono"
                  data-testid="coal-per-cost-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Per Transport Cost (₹/unit)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={perTransportCost}
                  onChange={(e) => setPerTransportCost(e.target.value)}
                  className="rounded-sm text-sm font-mono"
                  data-testid="coal-per-transport-cost-input"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            
            {/* Total Material */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Total Material (qty)</Label>
              <Input
                type="number"
                placeholder="0"
                value={totalMaterial}
                onChange={(e) => setTotalMaterial(e.target.value)}
                className="rounded-sm text-sm font-mono"
                data-testid="coal-total-material-input"
                min="0"
                step="0.01"
              />
            </div>
            
            {/* Sub Total, Paid, Due */}
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-amber-200">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Sub Total</Label>
                <div className="px-3 py-2 bg-white rounded-sm border border-slate-200 font-mono text-sm font-semibold text-slate-700">
                  {formatCurrency(coalSubTotal)}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Paid (₹)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={paid}
                  onChange={(e) => setPaid(e.target.value)}
                  className="rounded-sm text-sm font-mono"
                  data-testid="coal-paid-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Due</Label>
                <div className={`px-3 py-2 bg-white rounded-sm border font-mono text-sm font-semibold ${
                  coalDue > 0 ? 'border-red-300 text-red-600' : 'border-green-300 text-green-600'
                }`}>
                  {formatCurrency(coalDue)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* A1 Coal Payment Received - पैसा मिला */}
      {isPaymentReceived && (
        <div className="p-3 bg-blue-50 rounded-sm border border-blue-200">
          <p className="text-xs font-semibold text-blue-700 flex items-center gap-2 mb-3">
            <Receipt className="w-4 h-4" />
            पैसा मिला - PAYMENT RECEIVED
          </p>
          
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Party Name *</Label>
              {partyNamesList.length > 0 ? (
                <Select value={partyName} onValueChange={setPartyName}>
                  <SelectTrigger className="rounded-sm text-sm" data-testid="payment-party-select">
                    <SelectValue placeholder="Party चुनें जिसने पैसा दिया" />
                  </SelectTrigger>
                  <SelectContent>
                    {partyNamesList.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="किसने पैसा दिया?"
                  value={partyName}
                  onChange={(e) => setPartyName(e.target.value)}
                  className="rounded-sm text-sm"
                  data-testid="payment-party-name-input"
                />
              )}
            </div>
            
            {/* Payment Mode - Cash / Account */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Payment Mode *</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger className="rounded-sm text-sm" data-testid="payment-mode-select">
                  <SelectValue placeholder="Cash या Account?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">💵 Cash (नकद)</SelectItem>
                  <SelectItem value="account">🏦 Account (खाता)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Amount Received (₹) *</Label>
              <Input
                type="number"
                placeholder="कितना पैसा मिला?"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="rounded-sm text-sm font-mono text-lg"
                data-testid="payment-amount-input"
                min="1"
                step="1"
              />
            </div>
          </div>
        </div>
      )}

      {/* A1 Transport Income - कमाई */}
      {isTransportIncome && (
        <div className="p-3 bg-green-50 rounded-sm border border-green-200">
          <p className="text-xs font-semibold text-green-700 flex items-center gap-2 mb-3">
            <Truck className="w-4 h-4" />
            कमाई - INCOME
          </p>
          
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Gari No. *</Label>
              {vehiclesList.length > 0 ? (
                <Select value={vehicleNumber} onValueChange={setVehicleNumber}>
                  <SelectTrigger className="rounded-sm text-sm" data-testid="transport-vehicle-select">
                    <SelectValue placeholder="Gari No. चुनें" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehiclesList.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                    <SelectItem value="__new__">+ नई गाड़ी जोड़ें</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="MH 12 AB 1234"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  className="rounded-sm text-sm"
                  data-testid="transport-vehicle-input"
                />
              )}
              {vehicleNumber === "__new__" && (
                <Input
                  placeholder="नई गाड़ी का नंबर"
                  onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  className="rounded-sm text-sm mt-2"
                  data-testid="transport-new-vehicle-input"
                />
              )}
            </div>
            
            {/* Party Name for Income */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Party Name (Optional)</Label>
              {partyNamesList.length > 0 ? (
                <Select value={partyName} onValueChange={setPartyName}>
                  <SelectTrigger className="rounded-sm text-sm" data-testid="transport-income-party-select">
                    <SelectValue placeholder="Party चुनें" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Party --</SelectItem>
                    {partyNamesList.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                    <SelectItem value="__new__">+ नया Party जोड़ें</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Party का नाम (optional)"
                  value={partyName}
                  onChange={(e) => setPartyName(e.target.value)}
                  className="rounded-sm text-sm"
                  data-testid="transport-income-party-input"
                />
              )}
              {partyName === "__new__" && (
                <Input
                  placeholder="नया Party का नाम"
                  onChange={(e) => setPartyName(e.target.value)}
                  className="rounded-sm text-sm mt-2"
                />
              )}
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Kitna Kamaye (₹) *</Label>
              <Input
                type="number"
                placeholder="कितना कमाया?"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="rounded-sm text-sm font-mono text-lg"
                data-testid="transport-income-amount"
                min="1"
                step="1"
              />
            </div>
          </div>
        </div>
      )}

      {/* A1 Transport Expense - खर्चा */}
      {isTransportExpense && (
        <div className="p-3 bg-red-50 rounded-sm border border-red-200">
          <p className="text-xs font-semibold text-red-700 flex items-center gap-2 mb-3">
            <Truck className="w-4 h-4" />
            खर्चा - EXPENSE
          </p>
          
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Gari No. *</Label>
              {vehiclesList.length > 0 ? (
                <Select value={vehicleNumber} onValueChange={setVehicleNumber}>
                  <SelectTrigger className="rounded-sm text-sm" data-testid="transport-expense-vehicle-select">
                    <SelectValue placeholder="Gari No. चुनें" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehiclesList.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                    <SelectItem value="__new__">+ नई गाड़ी जोड़ें</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="MH 12 AB 1234"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  className="rounded-sm text-sm"
                  data-testid="transport-expense-vehicle-input"
                />
              )}
              {vehicleNumber === "__new__" && (
                <Input
                  placeholder="नई गाड़ी का नंबर"
                  onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  className="rounded-sm text-sm mt-2"
                />
              )}
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Expense Category *</Label>
              <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                <SelectTrigger className="rounded-sm text-sm" data-testid="transport-expense-category">
                  <SelectValue placeholder="खर्चा का प्रकार चुनें" />
                </SelectTrigger>
                <SelectContent>
                  {TRANSPORT_EXPENSE_CATEGORIES.map(cat => {
                    const IconComp = cat.icon;
                    return (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <IconComp className="w-4 h-4" />
                          {cat.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Fuel Vendor - only show when fuel/tell category selected */}
            {(expenseCategory === "fuel" || expenseCategory === "tell") && (
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Fuel Vendor (कहाँ से लिया?) *</Label>
                {fuelVendorsList.length > 0 ? (
                  <Select value={fuelVendor} onValueChange={setFuelVendor}>
                    <SelectTrigger className="rounded-sm text-sm" data-testid="fuel-vendor-select">
                      <SelectValue placeholder="Vendor चुनें (जैसे Dharaka)" />
                    </SelectTrigger>
                    <SelectContent>
                      {fuelVendorsList.map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                      <SelectItem value="__new__">+ नया Vendor जोड़ें</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Vendor का नाम (जैसे Dharaka)"
                    value={fuelVendor}
                    onChange={(e) => setFuelVendor(e.target.value)}
                    className="rounded-sm text-sm"
                    data-testid="fuel-vendor-input"
                  />
                )}
                {fuelVendor === "__new__" && (
                  <Input
                    placeholder="नया Vendor का नाम"
                    onChange={(e) => setFuelVendor(e.target.value)}
                    className="rounded-sm text-sm mt-2"
                  />
                )}
              </div>
            )}
            
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Amount (₹) *</Label>
              <Input
                type="number"
                placeholder="कितना खर्चा हुआ?"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="rounded-sm text-sm font-mono text-lg"
                data-testid="transport-expense-amount"
                min="1"
                step="1"
              />
            </div>
          </div>
        </div>
      )}

      {/* Show amount field only for non-coal and non-transport */}
      {!isCoalCompany && !isTransportCompany ? (
        <div className="space-y-2">
          <Label htmlFor="amount" className="text-xs uppercase tracking-wide text-slate-500">Amount (INR)</Label>
          <Input
            id="amount"
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="font-mono text-lg border-0 border-b border-slate-200 rounded-none focus:border-primary focus:ring-0"
            data-testid="transaction-amount-input"
            min="1"
            step="1"
          />
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="description" className="text-xs uppercase tracking-wide text-slate-500">Description <span className="text-slate-400">(Optional)</span></Label>
        <Input
          id="description"
          placeholder="Enter description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="border-0 border-b border-slate-200 rounded-none focus:border-primary focus:ring-0"
          data-testid="transaction-description-input"
        />
      </div>

      <Button 
        type="submit" 
        className={`w-full text-white rounded-sm uppercase tracking-wide font-semibold text-xs py-3 btn-active ${
          isPaymentReceived ? 'bg-blue-500 hover:bg-blue-600' : 
          isTransportIncome ? 'bg-green-500 hover:bg-green-600' :
          isTransportExpense ? 'bg-red-500 hover:bg-red-600' :
          isCoalCompany ? 'bg-amber-500 hover:bg-amber-600' : 
          'bg-primary hover:bg-primary/90'
        }`}
        disabled={loading}
        data-testid="submit-transaction-btn"
      >
        {loading ? "Adding..." : 
          isPaymentReceived ? 'पैसा मिला Entry करें' : 
          isTransportIncome ? 'कमाई Entry करें' :
          isTransportExpense ? 'खर्चा Entry करें' :
          `Add ${type === 'credit' ? 'Credit' : 'Debit'} Transaction`}
      </Button>
    </form>
  );
};

// Edit Transaction Form Component
const EditTransactionForm = ({ transaction, onSuccess, onClose }) => {
  const [type, setType] = useState(transaction.transaction_type || "credit");
  const [transactionDate, setTransactionDate] = useState(
    transaction.created_at ? new Date(transaction.created_at) : new Date()
  );
  const [loading, setLoading] = useState(false);
  
  // Fields
  const [perUnitCost, setPerUnitCost] = useState(transaction.per_unit_cost?.toString() || "");
  const [netUnit, setNetUnit] = useState(transaction.net_unit?.toString() || "");
  const [paid, setPaid] = useState(transaction.paid?.toString() || "");
  const [amount, setAmount] = useState(transaction.amount?.toString() || "");
  const [description, setDescription] = useState(transaction.description || "");
  const [partyName, setPartyName] = useState(transaction.party_name || "");
  const [material, setMaterial] = useState(transaction.category || "");

  // Calculate sub total and due
  const subTotal = perUnitCost && netUnit ? parseFloat(perUnitCost) * parseFloat(netUnit) : 0;
  const due = subTotal - (paid ? parseFloat(paid) : 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const finalAmount = subTotal > 0 ? subTotal : parseFloat(amount);
    
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please enter valid amount");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        transaction_type: type,
        amount: finalAmount,
        description: description || "",
        transaction_date: transactionDate ? format(transactionDate, 'yyyy-MM-dd') : null,
        category: material || null,
        per_unit_cost: perUnitCost ? parseFloat(perUnitCost) : null,
        net_unit: netUnit ? parseFloat(netUnit) : null,
        sub_total: subTotal || null,
        paid: paid ? parseFloat(paid) : null,
        due: due || null,
        party_name: partyName || null
      };

      await axios.put(`${API}/transactions/${transaction.id}`, payload);
      toast.success("Transaction updated successfully");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Failed to update transaction");
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Transaction Type</Label>
        <Tabs value={type} onValueChange={setType} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger 
              value="credit" 
              className="data-[state=active]:bg-green-50 data-[state=active]:text-accent-success"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" />
              Credit
            </TabsTrigger>
            <TabsTrigger 
              value="debit" 
              className="data-[state=active]:bg-red-50 data-[state=active]:text-accent-danger"
            >
              <ArrowDownRight className="w-4 h-4 mr-2" />
              Debit
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Transaction Date</Label>
        <div className="flex gap-2">
          <Input
            type="date"
            value={transactionDate ? format(transactionDate, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              if (e.target.value) {
                setTransactionDate(new Date(e.target.value));
              } else {
                setTransactionDate(new Date());
              }
            }}
            className="flex-1 rounded-sm font-mono"
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="icon" className="rounded-sm shrink-0">
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={transactionDate}
                onSelect={(date) => setTransactionDate(date || new Date())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="p-3 bg-amber-50 rounded-sm border border-amber-200">
        <p className="text-xs font-semibold text-amber-700 flex items-center gap-2 mb-3">
          <Package className="w-4 h-4" />
          DETAILS
        </p>
        
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Party Name</Label>
            <Input
              placeholder="Enter party name"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              className="rounded-sm text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Material</Label>
            <Input
              placeholder="Material type"
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              className="rounded-sm text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Per Unit Cost (₹)</Label>
            <Input
              type="number"
              placeholder="0"
              value={perUnitCost}
              onChange={(e) => setPerUnitCost(e.target.value)}
              className="rounded-sm text-sm font-mono"
              min="0"
              step="0.01"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Total Material</Label>
            <Input
              type="number"
              placeholder="0"
              value={netUnit}
              onChange={(e) => setNetUnit(e.target.value)}
              className="rounded-sm text-sm font-mono"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Sub Total</Label>
            <div className="px-3 py-2 bg-white rounded-sm border border-slate-200 font-mono text-sm font-semibold text-slate-700">
              {formatCurrency(subTotal)}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Paid (₹)</Label>
            <Input
              type="number"
              placeholder="0"
              value={paid}
              onChange={(e) => setPaid(e.target.value)}
              className="rounded-sm text-sm font-mono"
              min="0"
              step="0.01"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Due</Label>
            <div className={`px-3 py-2 bg-white rounded-sm border border-slate-200 font-mono text-sm font-semibold ${due > 0 ? 'text-accent-danger' : 'text-accent-success'}`}>
              {formatCurrency(due)}
            </div>
          </div>
        </div>
      </div>

      {!subTotal && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Amount (INR)</Label>
          <Input
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="font-mono text-lg border-0 border-b border-slate-200 rounded-none focus:border-primary focus:ring-0"
            min="1"
            step="1"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide text-slate-500">Description <span className="text-slate-400">(Optional)</span></Label>
        <Input
          placeholder="Enter description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="border-0 border-b border-slate-200 rounded-none focus:border-primary focus:ring-0"
        />
      </div>

      <div className="flex gap-3">
        <Button 
          type="button"
          variant="outline"
          className="flex-1 rounded-sm"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          className="flex-1 bg-primary text-white rounded-sm uppercase tracking-wide font-semibold text-xs hover:bg-primary/90"
          disabled={loading}
        >
          {loading ? "Updating..." : "Update Transaction"}
        </Button>
      </div>
    </form>
  );
};

// Transaction List Component
const TransactionList = ({ transactions, onDelete, onEdit, showCoalDetails = false, showTransportDetails = false }) => {
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filterParty, setFilterParty] = useState("all");
  const [filterVehicle, setFilterVehicle] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all"); // all, due, paid
  const [selectedParty, setSelectedParty] = useState(null);
  const [showPartyStatement, setShowPartyStatement] = useState(false);
  const [partyStatementData, setPartyStatementData] = useState(null);
  
  // Vehicle Statement states for A1 Transport
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showVehicleStatement, setShowVehicleStatement] = useState(false);
  const [vehicleStatementData, setVehicleStatementData] = useState(null);

  // Fetch party statement when selected
  useEffect(() => {
    if (selectedParty && showPartyStatement) {
      // Only fetch A1 Coal transactions, not A1 Transport auto-entries
      axios.get(`${API}/party-statement/${encodeURIComponent(selectedParty)}?company=A1%20Coal`)
        .then(res => setPartyStatementData(res.data))
        .catch(() => toast.error("Failed to load party statement"));
    }
  }, [selectedParty, showPartyStatement]);
  
  // Fetch vehicle statement when selected
  useEffect(() => {
    if (selectedVehicle && showVehicleStatement) {
      axios.get(`${API}/vehicle-statement/${encodeURIComponent(selectedVehicle)}`)
        .then(res => setVehicleStatementData(res.data))
        .catch(() => toast.error("Failed to load vehicle statement"));
    }
  }, [selectedVehicle, showVehicleStatement]);

  // Get unique vehicles for filter
  const uniqueVehicles = [...new Set(transactions.filter(t => t.vehicle_number).map(t => t.vehicle_number))];

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    if (filterParty !== "all" && t.party_name !== filterParty) return false;
    if (filterVehicle !== "all" && t.vehicle_number !== filterVehicle) return false;
    if (filterPayment === "due" && (!t.due || t.due <= 0)) return false;
    if (filterPayment === "paid" && t.due > 0) return false;
    return true;
  });

  if (transactions.length === 0) {
    return (
      <div className="empty-state py-12">
        <Receipt className="w-12 h-12 text-slate-300 mb-4" />
        <p className="text-lg font-display font-semibold text-slate-400">No transactions yet</p>
        <p className="text-sm text-slate-400">Add your first transaction to get started</p>
      </div>
    );
  }

  const getCategoryName = (category) => {
    // Check transport expense categories
    const transportCat = TRANSPORT_EXPENSE_CATEGORIES.find(c => c.id === category);
    if (transportCat) return transportCat.name;
    return category || '-';
  };

  const handleEditSuccess = () => {
    setEditingTransaction(null);
    onEdit();
  };

  const clearFilters = () => {
    setFilterParty("all");
    setFilterVehicle("all");
    setFilterPayment("all");
  };

  return (
    <>
      {/* Edit Dialog */}
      <Dialog open={!!editingTransaction} onOpenChange={(open) => !open && setEditingTransaction(null)}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Transaction
            </DialogTitle>
            <DialogDescription>
              Update the transaction details below
            </DialogDescription>
          </DialogHeader>
          {editingTransaction && (
            <EditTransactionForm 
              transaction={editingTransaction}
              onSuccess={handleEditSuccess}
              onClose={() => setEditingTransaction(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Party Statement Dialog */}
      <Dialog open={showPartyStatement} onOpenChange={(open) => {
        if (!open) {
          setShowPartyStatement(false);
          setSelectedParty(null);
          setPartyStatementData(null);
        }
      }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Party Statement - {selectedParty}
            </DialogTitle>
            <DialogDescription>
              {selectedParty} का पूरा हिसाब-किताब
            </DialogDescription>
          </DialogHeader>
          
          {partyStatementData ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-green-50 rounded-sm border border-green-200 text-center">
                  <p className="text-xs text-green-600 mb-1">कुल माल</p>
                  <p className="font-mono font-bold text-green-700">{formatCurrency(partyStatementData.summary?.total_material || 0)}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-sm border border-blue-200 text-center">
                  <p className="text-xs text-blue-600 mb-1">कुल पैसा मिला</p>
                  <p className="font-mono font-bold text-blue-700">{formatCurrency(partyStatementData.summary?.total_payment || 0)}</p>
                </div>
                <div className={`p-3 rounded-sm border text-center ${partyStatementData.summary?.due > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <p className={`text-xs mb-1 ${partyStatementData.summary?.due > 0 ? 'text-red-600' : 'text-green-600'}`}>बाकी</p>
                  <p className={`font-mono font-bold ${partyStatementData.summary?.due > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatCurrency(partyStatementData.summary?.due || 0)}</p>
                </div>
              </div>
              
              {/* Transactions Table */}
              <div className="border rounded-sm overflow-hidden overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Gari No</TableHead>
                      <TableHead className="text-xs text-right">Maal</TableHead>
                      <TableHead className="text-xs text-right">Per Rate</TableHead>
                      <TableHead className="text-xs text-right">Transport</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partyStatementData.transactions?.map((t, idx) => (
                      <TableRow key={t.id || idx} className={`text-sm ${t.transaction_type === 'debit' ? 'bg-blue-50' : ''}`}>
                        <TableCell className="text-xs">{formatDate(t.created_at)}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${t.transaction_type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {t.transaction_type === 'credit' ? 'माल दिया' : 'पैसा मिला'}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs font-mono">{t.vehicle_number || '-'}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{t.net_unit || t.net_material || '-'}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{t.per_unit_cost ? formatCurrency(t.per_unit_cost) : '-'}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{t.transport_cost ? formatCurrency(t.transport_cost) : '-'}</TableCell>
                        <TableCell className={`text-right font-mono text-xs font-semibold ${t.transaction_type === 'credit' ? 'text-green-600' : 'text-blue-600'}`}>
                          {t.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Print Button */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowPartyStatement(false);
                    setSelectedParty(null);
                    setPartyStatementData(null);
                  }}
                  className="rounded-sm"
                >
                  <X className="w-4 h-4 mr-1" />
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    // Format currency for print (plain number with Rs.)
                    const formatPrintCurrency = (amount) => {
                      return 'Rs. ' + new Intl.NumberFormat('en-IN', {
                        maximumFractionDigits: 0
                      }).format(amount);
                    };
                    
                    // Format date for print
                    const formatPrintDate = (dateStr) => {
                      const date = new Date(dateStr);
                      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                    };
                    
                    // Build table rows with detailed info
                    const tableRows = partyStatementData.transactions?.map(t => {
                      if (t.transaction_type === 'credit') {
                        // Maal Diya - show all details
                        return `<tr>
                          <td>${formatPrintDate(t.created_at)}</td>
                          <td>Maal Diya</td>
                          <td>${t.vehicle_number || '-'}</td>
                          <td class="text-right">${t.net_unit || t.net_material || '-'}</td>
                          <td class="text-right">${t.per_unit_cost ? formatPrintCurrency(t.per_unit_cost) : '-'}</td>
                          <td class="text-right">${t.transport_cost ? formatPrintCurrency(t.transport_cost) : '-'}</td>
                          <td class="text-right credit">+${formatPrintCurrency(t.amount)}</td>
                        </tr>`;
                      } else {
                        // Paisa Mila - payment row
                        return `<tr class="payment-row">
                          <td>${formatPrintDate(t.created_at)}</td>
                          <td>Paisa Mila</td>
                          <td colspan="4">${t.description || 'Payment Received'}</td>
                          <td class="text-right debit">-${formatPrintCurrency(t.amount)}</td>
                        </tr>`;
                      }
                    }).join('') || '';
                    
                    // Generate clean print HTML
                    const printHTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Party Statement - ${selectedParty}</title>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: Arial, Helvetica, sans-serif; padding: 20px; color: #333; font-size: 11px; }
h1 { font-size: 16px; margin-bottom: 3px; }
h2 { font-size: 12px; color: #555; margin-bottom: 15px; font-weight: normal; }
.summary { display: flex; gap: 10px; margin-bottom: 15px; }
.summary-card { padding: 8px 10px; border: 1px solid #ccc; text-align: center; flex: 1; }
.summary-card.green { background: #e8f5e9; border-color: #4caf50; }
.summary-card.blue { background: #e3f2fd; border-color: #2196f3; }
.summary-card.red { background: #ffebee; border-color: #f44336; }
.summary-label { font-size: 9px; color: #666; margin-bottom: 3px; }
.summary-value { font-size: 13px; font-weight: bold; }
.green .summary-value { color: #2e7d32; }
.blue .summary-value { color: #1565c0; }
.red .summary-value { color: #c62828; }
table { width: 100%; border-collapse: collapse; margin-top: 10px; }
th, td { border: 1px solid #bbb; padding: 5px 6px; text-align: left; font-size: 10px; }
th { background: #f0f0f0; font-weight: bold; font-size: 9px; }
.credit { color: #2e7d32; font-weight: bold; }
.debit { color: #1565c0; font-weight: bold; }
.text-right { text-align: right; }
.payment-row { background: #f5f5f5; }
.footer { margin-top: 20px; font-size: 9px; color: #888; text-align: center; border-top: 1px solid #ddd; padding-top: 8px; }
@media print { body { padding: 10px; } }
</style>
</head>
<body>
<h1>Party Statement</h1>
<h2>${selectedParty} - Date: ${new Date().toLocaleDateString('en-IN')}</h2>

<div class="summary">
<div class="summary-card green">
<div class="summary-label">Total Material (Kul Maal)</div>
<div class="summary-value">${formatPrintCurrency(partyStatementData.summary?.total_material || 0)}</div>
</div>
<div class="summary-card blue">
<div class="summary-label">Total Payment (Paisa Mila)</div>
<div class="summary-value">${formatPrintCurrency(partyStatementData.summary?.total_payment || 0)}</div>
</div>
<div class="summary-card ${partyStatementData.summary?.due > 0 ? 'red' : 'green'}">
<div class="summary-label">Due Amount (Baaki)</div>
<div class="summary-value">${formatPrintCurrency(partyStatementData.summary?.due || 0)}</div>
</div>
</div>

<table>
<thead>
<tr>
<th>Date</th>
<th>Type</th>
<th>Gari No</th>
<th class="text-right">Maal (Qty)</th>
<th class="text-right">Per Rate</th>
<th class="text-right">Transport</th>
<th class="text-right">Amount</th>
</tr>
</thead>
<tbody>
${tableRows}
</tbody>
</table>

<div class="footer">Generated: ${new Date().toLocaleString('en-IN')} | A1 Coal Ledger</div>
</body>
</html>`;
                    
                    // Use window.open for better Safari/MacBook compatibility
                    const printWindow = window.open('', '_blank', 'width=800,height=600');
                    if (printWindow) {
                      printWindow.document.write(printHTML);
                      printWindow.document.close();
                      printWindow.onload = () => {
                        setTimeout(() => {
                          printWindow.focus();
                          printWindow.print();
                          printWindow.onafterprint = () => printWindow.close();
                        }, 300);
                      };
                    } else {
                      // Fallback to iframe if popup blocked
                      const iframe = document.createElement('iframe');
                      iframe.style.position = 'absolute';
                      iframe.style.width = '0';
                      iframe.style.height = '0';
                      iframe.style.border = 'none';
                      iframe.style.left = '-9999px';
                      document.body.appendChild(iframe);
                      
                      const iframeDoc = iframe.contentWindow.document;
                      iframeDoc.open();
                      iframeDoc.write(printHTML);
                      iframeDoc.close();
                      
                      iframe.onload = () => {
                        setTimeout(() => {
                          iframe.contentWindow.focus();
                          iframe.contentWindow.print();
                          setTimeout(() => document.body.removeChild(iframe), 1000);
                        }, 250);
                      };
                    }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                  data-testid="print-party-statement-btn"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print Statement
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-sm text-slate-500">Loading statement...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vehicle Statement Dialog for A1 Transport */}
      <Dialog open={showVehicleStatement} onOpenChange={(open) => {
        if (!open) {
          setShowVehicleStatement(false);
          setSelectedVehicle(null);
          setVehicleStatementData(null);
        }
      }}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Vehicle Statement - {selectedVehicle}
            </DialogTitle>
            <DialogDescription>
              {selectedVehicle} का पूरा हिसाब-किताब
            </DialogDescription>
          </DialogHeader>
          
          {vehicleStatementData ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-green-50 rounded-sm border border-green-200 text-center">
                  <p className="text-xs text-green-600 mb-1">कुल कमाई (Income)</p>
                  <p className="font-mono font-bold text-green-700">{formatCurrency(vehicleStatementData.summary?.total_income || 0)}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-sm border border-red-200 text-center">
                  <p className="text-xs text-red-600 mb-1">कुल खर्चा (Expense)</p>
                  <p className="font-mono font-bold text-red-700">{formatCurrency(vehicleStatementData.summary?.total_expense || 0)}</p>
                </div>
                <div className={`p-3 rounded-sm border text-center ${vehicleStatementData.summary?.profit >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-xs mb-1 ${vehicleStatementData.summary?.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>बचत (Profit)</p>
                  <p className={`font-mono font-bold ${vehicleStatementData.summary?.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(vehicleStatementData.summary?.profit || 0)}</p>
                </div>
              </div>
              
              {/* Expense Breakdown */}
              {vehicleStatementData.summary?.expense_breakdown && Object.keys(vehicleStatementData.summary.expense_breakdown).length > 0 && (
                <div className="p-3 bg-slate-50 rounded-sm border">
                  <p className="text-xs font-semibold text-slate-600 mb-2">खर्चा Breakdown:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(vehicleStatementData.summary.expense_breakdown).map(([cat, amount]) => {
                      const catName = TRANSPORT_EXPENSE_CATEGORIES.find(c => c.id === cat)?.name || cat;
                      return (
                        <span key={cat} className="text-xs px-2 py-1 bg-white border rounded">
                          {catName}: <span className="font-mono text-red-600">{formatCurrency(amount)}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Transactions Table */}
              <div className="border rounded-sm overflow-hidden overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Category</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicleStatementData.transactions?.map((t, idx) => (
                      <TableRow key={t.id || idx} className={`text-sm ${t.transaction_type === 'debit' ? 'bg-red-50' : ''}`}>
                        <TableCell className="text-xs">{formatDate(t.created_at)}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${t.transaction_type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {t.transaction_type === 'credit' ? 'कमाई' : 'खर्चा'}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">{TRANSPORT_EXPENSE_CATEGORIES.find(c => c.id === t.category)?.name || t.category || '-'}</TableCell>
                        <TableCell className="text-xs text-slate-600">{t.description || '-'}</TableCell>
                        <TableCell className={`text-right font-mono text-xs font-semibold ${t.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {t.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Print Button */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowVehicleStatement(false);
                    setSelectedVehicle(null);
                    setVehicleStatementData(null);
                  }}
                  className="rounded-sm"
                >
                  <X className="w-4 h-4 mr-1" />
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    // Format currency for print
                    const formatPrintCurrency = (amount) => {
                      return 'Rs. ' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount);
                    };
                    const formatPrintDate = (dateStr) => {
                      const date = new Date(dateStr);
                      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                    };
                    
                    const tableRows = vehicleStatementData.transactions?.map(t => 
                      `<tr class="${t.transaction_type === 'debit' ? 'expense-row' : ''}">
                        <td>${formatPrintDate(t.created_at)}</td>
                        <td>${t.transaction_type === 'credit' ? 'Income (Kamai)' : 'Expense (Kharcha)'}</td>
                        <td>${TRANSPORT_EXPENSE_CATEGORIES.find(c => c.id === t.category)?.name || t.category || '-'}</td>
                        <td>${t.description || '-'}</td>
                        <td class="text-right ${t.transaction_type === 'credit' ? 'credit' : 'debit'}">
                          ${t.transaction_type === 'credit' ? '+' : '-'}${formatPrintCurrency(t.amount)}
                        </td>
                      </tr>`
                    ).join('') || '';
                    
                    const printHTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Vehicle Statement - ${selectedVehicle}</title>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: Arial, Helvetica, sans-serif; padding: 20px; color: #333; font-size: 11px; }
h1 { font-size: 16px; margin-bottom: 3px; }
h2 { font-size: 12px; color: #555; margin-bottom: 15px; font-weight: normal; }
.summary { display: flex; gap: 10px; margin-bottom: 15px; }
.summary-card { padding: 8px 10px; border: 1px solid #ccc; text-align: center; flex: 1; }
.summary-card.green { background: #e8f5e9; border-color: #4caf50; }
.summary-card.red { background: #ffebee; border-color: #f44336; }
.summary-label { font-size: 9px; color: #666; margin-bottom: 3px; }
.summary-value { font-size: 13px; font-weight: bold; }
.green .summary-value { color: #2e7d32; }
.red .summary-value { color: #c62828; }
table { width: 100%; border-collapse: collapse; margin-top: 10px; }
th, td { border: 1px solid #bbb; padding: 5px 6px; text-align: left; font-size: 10px; }
th { background: #f0f0f0; font-weight: bold; font-size: 9px; }
.credit { color: #2e7d32; font-weight: bold; }
.debit { color: #c62828; font-weight: bold; }
.text-right { text-align: right; }
.expense-row { background: #fff5f5; }
.footer { margin-top: 20px; font-size: 9px; color: #888; text-align: center; border-top: 1px solid #ddd; padding-top: 8px; }
</style>
</head>
<body>
<h1>Vehicle Statement</h1>
<h2>${selectedVehicle} - Date: ${new Date().toLocaleDateString('en-IN')}</h2>

<div class="summary">
<div class="summary-card green">
<div class="summary-label">Total Income (Kul Kamai)</div>
<div class="summary-value">${formatPrintCurrency(vehicleStatementData.summary?.total_income || 0)}</div>
</div>
<div class="summary-card red">
<div class="summary-label">Total Expense (Kul Kharcha)</div>
<div class="summary-value">${formatPrintCurrency(vehicleStatementData.summary?.total_expense || 0)}</div>
</div>
<div class="summary-card ${vehicleStatementData.summary?.profit >= 0 ? 'green' : 'red'}">
<div class="summary-label">Profit (Bachat)</div>
<div class="summary-value">${formatPrintCurrency(vehicleStatementData.summary?.profit || 0)}</div>
</div>
</div>

<table>
<thead>
<tr><th>Date</th><th>Type</th><th>Category</th><th>Description</th><th class="text-right">Amount</th></tr>
</thead>
<tbody>
${tableRows}
</tbody>
</table>

<div class="footer">Generated: ${new Date().toLocaleString('en-IN')} | A1 Transport Ledger</div>
</body>
</html>`;
                    
                    // Use window.open for better Safari/MacBook compatibility
                    const printWindow = window.open('', '_blank', 'width=800,height=600');
                    if (printWindow) {
                      printWindow.document.write(printHTML);
                      printWindow.document.close();
                      printWindow.onload = () => {
                        setTimeout(() => {
                          printWindow.focus();
                          printWindow.print();
                          printWindow.onafterprint = () => printWindow.close();
                        }, 300);
                      };
                    } else {
                      // Fallback to iframe if popup blocked
                      const iframe = document.createElement('iframe');
                      iframe.style.position = 'absolute';
                      iframe.style.width = '0';
                      iframe.style.height = '0';
                      iframe.style.border = 'none';
                      iframe.style.left = '-9999px';
                      document.body.appendChild(iframe);
                      
                      const iframeDoc = iframe.contentWindow.document;
                      iframeDoc.open();
                      iframeDoc.write(printHTML);
                      iframeDoc.close();
                      
                      iframe.onload = () => {
                        setTimeout(() => {
                          iframe.contentWindow.focus();
                          iframe.contentWindow.print();
                          setTimeout(() => document.body.removeChild(iframe), 1000);
                        }, 250);
                      };
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                  data-testid="print-vehicle-statement-btn"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print Statement
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-sm text-slate-500">Loading statement...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Filters for A1 Coal */}
      {showCoalDetails && (
        <div className="p-4 bg-amber-50 border-b border-amber-200">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-semibold text-amber-600 uppercase">Filters:</span>
            </div>
            
            <Select value={filterParty} onValueChange={setFilterParty}>
              <SelectTrigger className="w-44 h-8 text-xs rounded-sm border-amber-300">
                <SelectValue placeholder="Party Name" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Parties</SelectItem>
                {[...new Set(transactions.filter(t => t.party_name).map(t => t.party_name))].map(name => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterPayment} onValueChange={setFilterPayment}>
              <SelectTrigger className="w-36 h-8 text-xs rounded-sm border-amber-300">
                <SelectValue placeholder="Payment Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="due">Due Only</SelectItem>
                <SelectItem value="paid">Paid Only</SelectItem>
              </SelectContent>
            </Select>
            
            {(filterParty !== "all" || filterPayment !== "all") && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs text-amber-700 hover:text-amber-800 hover:bg-amber-100">
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
            
            <span className="text-xs text-amber-600 ml-auto">
              Showing {filteredTransactions.length} of {transactions.length}
            </span>
          </div>
        </div>
      )}

      {/* Party Summary for A1 Coal */}
      {showCoalDetails && transactions.length > 0 && (
        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
          <p className="text-xs font-semibold text-amber-700 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" />
            PARTY SUMMARY - कौन कितना बाकी है
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(() => {
              // Calculate party-wise summary
              const partySummary = {};
              transactions.forEach(t => {
                if (t.party_name) {
                  if (!partySummary[t.party_name]) {
                    partySummary[t.party_name] = { totalMaterial: 0, totalPayment: 0 };
                  }
                  if (t.transaction_type === 'credit') {
                    partySummary[t.party_name].totalMaterial += t.amount || 0;
                  } else if (t.transaction_type === 'debit' && t.category === 'payment_received') {
                    partySummary[t.party_name].totalPayment += t.amount || 0;
                  }
                }
              });
              
              return Object.entries(partySummary).map(([party, data]) => {
                const due = data.totalMaterial - data.totalPayment;
                return (
                  <div key={party} className={`p-3 rounded-sm border ${due > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => {
                      setSelectedParty(party);
                      setShowPartyStatement(true);
                    }}
                  >
                    <p className="text-xs font-semibold text-slate-700 truncate">{party}</p>
                    <div className="mt-1 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">माल:</span>
                        <span className="font-mono text-green-600">{formatCurrency(data.totalMaterial)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">पैसा:</span>
                        <span className="font-mono text-blue-600">{formatCurrency(data.totalPayment)}</span>
                      </div>
                      <div className={`flex justify-between text-xs font-semibold pt-1 border-t ${due > 0 ? 'border-red-200' : 'border-green-200'}`}>
                        <span className={due > 0 ? 'text-red-600' : 'text-green-600'}>बाकी:</span>
                        <span className={`font-mono ${due > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(due)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-center text-slate-400 mt-2">Click for Statement</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Vehicle Summary for A1 Transport */}
      {showTransportDetails && transactions.length > 0 && (
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
          <p className="text-xs font-semibold text-blue-700 mb-3 flex items-center gap-2">
            <Truck className="w-4 h-4" />
            VEHICLE SUMMARY - गाड़ी वार हिसाब (Click for Details)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(() => {
              // Calculate vehicle-wise summary
              const vehicleSummary = {};
              transactions.forEach(t => {
                if (t.vehicle_number) {
                  if (!vehicleSummary[t.vehicle_number]) {
                    vehicleSummary[t.vehicle_number] = { income: 0, expense: 0 };
                  }
                  if (t.transaction_type === 'credit') {
                    vehicleSummary[t.vehicle_number].income += t.amount || 0;
                  } else if (t.transaction_type === 'debit') {
                    vehicleSummary[t.vehicle_number].expense += t.amount || 0;
                  }
                }
              });
              
              return Object.entries(vehicleSummary).map(([vehicle, data]) => {
                const profit = data.income - data.expense;
                return (
                  <div 
                    key={vehicle} 
                    className={`p-3 rounded-sm border cursor-pointer hover:shadow-md transition-shadow ${profit > 0 ? 'bg-green-50 border-green-200' : profit < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setShowVehicleStatement(true);
                    }}
                  >
                    <p className="text-xs font-semibold text-slate-700 truncate flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      {vehicle}
                    </p>
                    <div className="mt-1 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">कमाई:</span>
                        <span className="font-mono text-green-600">{formatCurrency(data.income)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">खर्चा:</span>
                        <span className="font-mono text-red-600">{formatCurrency(data.expense)}</span>
                      </div>
                      <div className={`flex justify-between text-xs font-semibold pt-1 border-t ${profit > 0 ? 'border-green-200' : 'border-red-200'}`}>
                        <span className={profit > 0 ? 'text-green-600' : 'text-red-600'}>बचत:</span>
                        <span className={`font-mono ${profit > 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(profit)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-center text-slate-400 mt-2">Click for Statement</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Vehicle Filter for A1 Transport */}
      {showTransportDetails && uniqueVehicles.length > 0 && (
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-semibold text-blue-500 uppercase">Filters:</span>
            </div>
            
            <Select value={filterVehicle} onValueChange={setFilterVehicle}>
              <SelectTrigger className="w-44 h-8 text-xs rounded-sm border-blue-300">
                <SelectValue placeholder="All Vehicles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vehicles</SelectItem>
                {uniqueVehicles.map(v => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {filterVehicle !== "all" && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs text-blue-700 hover:text-blue-800 hover:bg-blue-100">
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
            
            <span className="text-xs text-blue-600 ml-auto">
              Showing {filteredTransactions.length} of {transactions.length}
            </span>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-200">
              <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Date</TableHead>
              {showCoalDetails && (
                <>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Party</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Material</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Gari No</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Per Cost</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Per Trans.</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Total Mat.</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Sub Total</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Paid</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Due</TableHead>
                </>
              )}
              {showTransportDetails && (
                <>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Gari No</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Description</TableHead>
                </>
              )}
              {!showCoalDetails && !showTransportDetails && (
                <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Description</TableHead>
              )}
              <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTransactions.map((transaction) => {
              const isCredit = transaction.transaction_type === "credit";
              
              return (
                <TableRow 
                  key={transaction.id} 
                  className="table-row-hover border-b border-slate-100"
                  data-testid={`transaction-row-${transaction.id}`}
                >
                  <TableCell className="font-mono text-sm">
                    <div>
                      <span className="text-slate-900">{formatDate(transaction.created_at)}</span>
                      <br />
                      <span className="text-slate-400 text-xs">{formatTime(transaction.created_at)}</span>
                    </div>
                  </TableCell>
                  {showCoalDetails && (
                    <>
                      <TableCell>
                        {transaction.party_name ? (
                          <span className="text-sm font-medium text-slate-700">{transaction.party_name}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.category === 'payment_received' ? (
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-semibold">
                            💰 पैसा मिला
                          </span>
                        ) : transaction.category === 'coal' ? (
                          <span className="px-2 py-1 bg-gray-800 text-white rounded text-xs font-semibold">
                            Coal
                          </span>
                        ) : transaction.category === 'sand' ? (
                          <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-semibold">
                            Sand
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.category === 'payment_received' ? (
                          <span className="text-slate-400">-</span>
                        ) : transaction.vehicle_number ? (
                          <span className="font-mono text-xs text-slate-700">{transaction.vehicle_number}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {transaction.category === 'payment_received' ? '-' : transaction.per_unit_cost ? formatCurrency(transaction.per_unit_cost) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {transaction.category === 'payment_received' ? '-' : transaction.transport_cost ? formatCurrency(transaction.transport_cost) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {transaction.category === 'payment_received' ? '-' : transaction.net_unit || '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">
                        {transaction.category === 'payment_received' ? '-' : transaction.sub_total ? formatCurrency(transaction.sub_total) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-600">
                        {transaction.category === 'payment_received' ? '-' : transaction.paid ? formatCurrency(transaction.paid) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {transaction.category === 'payment_received' ? '-' : transaction.due !== null && transaction.due !== undefined ? (
                          <span className={transaction.due > 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                            {formatCurrency(transaction.due)}
                          </span>
                        ) : '-'}
                      </TableCell>
                    </>
                  )}
                  {showTransportDetails && (
                    <>
                      <TableCell>
                        {transaction.vehicle_number ? (
                          <span className="font-mono text-xs font-semibold text-slate-700 flex items-center gap-1">
                            <Truck className="w-3 h-3 text-blue-500" />
                            {transaction.vehicle_number}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.transaction_type === 'credit' ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                            कमाई
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                            {getCategoryName(transaction.category)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-body">
                        <span className="text-slate-700">{transaction.description || '-'}</span>
                      </TableCell>
                    </>
                  )}
                  {!showCoalDetails && !showTransportDetails && (
                    <TableCell className="font-body">
                      <span className="text-slate-700">{transaction.description || '-'}</span>
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <span className={`font-mono font-semibold ${
                      isCredit ? 'text-accent-success' : 'text-accent-danger'
                    }`}>
                      {isCredit ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {showCoalDetails && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingTransaction(transaction)}
                          className="text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                          data-testid={`edit-transaction-${transaction.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(transaction.id)}
                        className="text-slate-400 hover:text-accent-danger hover:bg-red-50"
                        data-testid={`delete-transaction-${transaction.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

// Statement View Component
const StatementView = () => {
  const [statementCompany, setStatementCompany] = useState("all");
  const [transactionType, setTransactionType] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statementData, setStatementData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statementCompany !== "all") {
        params.company = statementCompany;
      }
      if (transactionType !== "all") {
        params.transaction_type = transactionType;
      }
      if (startDate) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
      }
      if (endDate) {
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }
      
      const response = await axios.get(`${API}/statement`, { params });
      setStatementData(response.data);
    } catch (error) {
      toast.error("Failed to fetch statement");
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [statementCompany, transactionType, startDate, endDate]);

  const handleDownload = async (format = 'csv') => {
    try {
      const params = new URLSearchParams();
      if (statementCompany !== "all") {
        params.append('company', statementCompany);
      }
      if (startDate) {
        params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      }
      
      const endpoint = format === 'pdf' ? 'statement/download-pdf' : 'statement/download';
      const url = `${API}/${endpoint}?${params.toString()}`;
      
      // Create a hidden anchor and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement_${statementCompany}_${format(new Date(), 'yyyyMMdd')}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Statement ${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      toast.error("Failed to download statement");
      // Error handled silently
    }
  };

  const handleDownloadCSV = () => {
    try {
      const params = new URLSearchParams();
      if (statementCompany !== "all") {
        params.append('company', statementCompany);
      }
      if (transactionType !== "all") {
        params.append('transaction_type', transactionType);
      }
      if (startDate) {
        params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      }
      
      const url = `${API}/statement/download?${params.toString()}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV downloaded successfully");
    } catch (error) {
      toast.error("Failed to download CSV");
      // Error handled silently
    }
  };

  const handleDownloadPDF = () => {
    try {
      const params = new URLSearchParams();
      if (statementCompany !== "all") {
        params.append('company', statementCompany);
      }
      if (transactionType !== "all") {
        params.append('transaction_type', transactionType);
      }
      if (startDate) {
        params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      }
      
      const url = `${API}/statement/download-pdf?${params.toString()}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
      // Error handled silently
    }
  };

  const handlePrint = () => {
    if (!statementData) return;

    const companyText = statementCompany === "all" ? "All Companies" : `${statementCompany} Company`;
    const typeText = transactionType === "all" ? "All Types" : transactionType === "credit" ? "Credit Only" : "Debit Only";
    const dateRange = startDate || endDate 
      ? `${startDate ? format(startDate, 'dd MMM yyyy') : 'Start'} - ${endDate ? format(endDate, 'dd MMM yyyy') : 'End'}`
      : 'All Dates';

    // Build table rows safely by escaping HTML
    const escapeHtml = (text) => {
      if (text === null || text === undefined) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };

    const tableRows = statementData.transactions.map(t => {
      const isCredit = t.transaction_type === 'credit';
      return `
        <tr>
          <td>${escapeHtml(format(new Date(t.created_at), 'dd MMM yyyy'))}</td>
          <td>${escapeHtml(t.company)}</td>
          <td><span class="type-${isCredit ? 'credit' : 'debit'}">${isCredit ? 'Credit' : 'Debit'}</span></td>
          <td>${escapeHtml(t.description || '-')}</td>
          <td class="amount-${isCredit ? 'credit' : 'debit'}">${isCredit ? '+' : '-'}₹${escapeHtml(t.amount?.toLocaleString('en-IN'))}</td>
        </tr>
      `;
    }).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Statement - Dual Ledger</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap');
          
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body {
            font-family: 'Bookman Old Style', 'Libre Baskerville', Georgia, serif;
            padding: 40px;
            color: #111;
            line-height: 1.5;
          }
          
          .header {
            text-align: center;
            border-bottom: 2px solid #0055FF;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .header h1 {
            font-size: 28px;
            color: #0055FF;
            margin-bottom: 5px;
          }
          
          .header p {
            color: #64748B;
            font-size: 12px;
          }
          
          .filters {
            background: #F8FAFC;
            padding: 15px 20px;
            border-radius: 4px;
            margin-bottom: 25px;
            font-size: 13px;
          }
          
          .filters span {
            margin-right: 20px;
            color: #334155;
          }
          
          .filters strong {
            color: #0F172A;
          }
          
          .summary {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            gap: 15px;
          }
          
          .summary-card {
            flex: 1;
            padding: 15px;
            border: 1px solid #E2E8F0;
            border-radius: 4px;
            text-align: center;
          }
          
          .summary-card .label {
            font-size: 11px;
            text-transform: uppercase;
            color: #64748B;
            letter-spacing: 1px;
            margin-bottom: 5px;
          }
          
          .summary-card .value {
            font-size: 20px;
            font-weight: bold;
          }
          
          .summary-card .value.credit { color: #00C853; }
          .summary-card .value.debit { color: #FF3D00; }
          .summary-card .value.balance { color: #0055FF; }
          
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
          }
          
          th {
            background: #0F172A;
            color: white;
            padding: 12px 10px;
            text-align: left;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          th:last-child { text-align: right; }
          
          td {
            padding: 10px;
            border-bottom: 1px solid #E2E8F0;
          }
          
          td:last-child { text-align: right; }
          
          tr:nth-child(even) { background: #F8FAFC; }
          
          .type-credit {
            background: #E8F5E9;
            color: #00C853;
            padding: 3px 8px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
          }
          
          .type-debit {
            background: #FFEBEE;
            color: #FF3D00;
            padding: 3px 8px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
          }
          
          .amount-credit { color: #00C853; font-weight: bold; font-family: monospace; }
          .amount-debit { color: #FF3D00; font-weight: bold; font-family: monospace; }
          
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #E2E8F0;
            text-align: center;
            font-size: 11px;
            color: #64748B;
          }
          
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Dual Ledger</h1>
          <p>Financial Statement - AR, A1 INT, A1 Coal & A1 Transport</p>
        </div>
        
        <div class="filters">
          <span><strong>Company:</strong> ${companyText}</span>
          <span><strong>Type:</strong> ${typeText}</span>
          <span><strong>Period:</strong> ${dateRange}</span>
          <span><strong>Generated:</strong> ${format(new Date(), 'dd MMM yyyy, HH:mm')}</span>
        </div>
        
        <div class="summary">
          <div class="summary-card">
            <div class="label">Total Credit</div>
            <div class="value credit">₹${statementData.summary.total_credit.toLocaleString('en-IN')}</div>
          </div>
          <div class="summary-card">
            <div class="label">Total Debit</div>
            <div class="value debit">₹${statementData.summary.total_debit.toLocaleString('en-IN')}</div>
          </div>
          <div class="summary-card">
            <div class="label">Net Balance</div>
            <div class="value balance">₹${statementData.summary.net_balance.toLocaleString('en-IN')}</div>
          </div>
          <div class="summary-card">
            <div class="label">Transactions</div>
            <div class="value">${statementData.summary.transaction_count}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Company</th>
              <th>Type</th>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Dual Ledger - Financial Management for AR, A1 INT, A1 Coal & A1 Transport</p>
          <p>This is a computer generated statement</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    // Safe print using Blob URL instead of document.write (XSS prevention)
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    
    if (printWindow) {
      printWindow.onafterprint = () => {
        URL.revokeObjectURL(url);
      };
      toast.success("Print dialog opened");
    } else {
      URL.revokeObjectURL(url);
      toast.error("Could not open print window. Please allow popups.");
    }
  };

  const clearFilters = () => {
    setStatementCompany("all");
    setTransactionType("all");
    setStartDate(null);
    setEndDate(null);
    // Fetch all data after clearing filters
    setLoading(true);
    axios.get(`${API}/statement`)
      .then(response => {
        setStatementData(response.data);
      })
      .catch(error => {
        toast.error("Failed to fetch statement");
        // Error handled silently
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="border border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Statement Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            {/* Company Filter */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Company</Label>
              <Select value={statementCompany} onValueChange={setStatementCompany}>
                <SelectTrigger data-testid="statement-company-filter" className="rounded-sm">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  <SelectItem value="AR">AR</SelectItem>
                  <SelectItem value="A1 INT">A1 INT</SelectItem>
                  <SelectItem value="A1 Coal">A1 Coal</SelectItem>
                  <SelectItem value="A1 Transport">A1 Transport</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Type Filter */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Type</Label>
              <Select value={transactionType} onValueChange={setTransactionType}>
                <SelectTrigger data-testid="statement-type-filter" className="rounded-sm">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="credit">Credit Only</SelectItem>
                  <SelectItem value="debit">Debit Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">From Date</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setStartDate(new Date(e.target.value));
                    } else {
                      setStartDate(null);
                    }
                  }}
                  className="flex-1 rounded-sm font-mono"
                  data-testid="statement-start-date-input"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-sm shrink-0"
                      data-testid="statement-start-date-calendar"
                    >
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {startDate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setStartDate(null)}
                    className="rounded-sm shrink-0 text-slate-400 hover:text-accent-danger hover:bg-red-50"
                    data-testid="clear-start-date"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">To Date</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setEndDate(new Date(e.target.value));
                    } else {
                      setEndDate(null);
                    }
                  }}
                  className="flex-1 rounded-sm font-mono"
                  data-testid="statement-end-date-input"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-sm shrink-0"
                      data-testid="statement-end-date-calendar"
                    >
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {endDate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEndDate(null)}
                    className="rounded-sm shrink-0 text-slate-400 hover:text-accent-danger hover:bg-red-50"
                    data-testid="clear-end-date"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button 
                onClick={fetchStatement}
                className="flex-1 bg-primary text-white rounded-sm uppercase tracking-wide font-semibold text-xs hover:bg-primary/90"
                data-testid="apply-statement-filter"
              >
                <Filter className="w-4 h-4 mr-2" />
                Apply
              </Button>
              <Button 
                variant="outline"
                onClick={clearFilters}
                className="rounded-sm"
                data-testid="clear-statement-filter"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statement Summary */}
      {statementData && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Total Credit</p>
                <p className="font-mono text-xl font-bold text-accent-success">
                  {formatCurrency(statementData.summary.total_credit)}
                </p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Total Debit</p>
                <p className="font-mono text-xl font-bold text-accent-danger">
                  {formatCurrency(statementData.summary.total_debit)}
                </p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Net Balance</p>
                <p className={`font-mono text-xl font-bold ${
                  statementData.summary.net_balance >= 0 ? 'text-accent-success' : 'text-accent-danger'
                }`}>
                  {formatCurrency(statementData.summary.net_balance)}
                </p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Transactions</p>
                <p className="font-mono text-xl font-bold text-slate-900">
                  {statementData.summary.transaction_count}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Download Buttons */}
          <div className="flex justify-end gap-3">
            <Button 
              onClick={handlePrint}
              variant="outline"
              className="rounded-sm uppercase tracking-wide font-semibold text-xs border-slate-300 hover:bg-slate-50"
              data-testid="print-statement-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button 
              onClick={handleDownloadCSV}
              variant="outline"
              className="rounded-sm uppercase tracking-wide font-semibold text-xs border-slate-300 hover:bg-slate-50"
              data-testid="download-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button 
              onClick={handleDownloadPDF}
              className="bg-slate-900 text-white rounded-sm uppercase tracking-wide font-semibold text-xs hover:bg-slate-800"
              data-testid="download-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>

          {/* Statement Table */}
          <Card className="border border-slate-200 shadow-sm rounded-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-statement font-bold text-lg tracking-tight">
                Statement Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {statementData.transactions.length === 0 ? (
                <div className="empty-state py-12">
                  <FileText className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-lg font-statement font-semibold text-slate-400">No transactions found</p>
                  <p className="text-sm text-slate-400">Try adjusting your filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto font-statement">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-slate-200">
                        <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Date</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Company</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Type</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Description</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {statementData.transactions.map((transaction) => {
                        const isCredit = transaction.transaction_type === "credit";
                        return (
                          <TableRow 
                            key={transaction.id} 
                            className="table-row-hover border-b border-slate-100"
                          >
                            <TableCell className="font-mono text-sm">
                              {formatDate(transaction.created_at)}
                            </TableCell>
                            <TableCell>
                              <span className="px-2 py-1 bg-slate-100 rounded text-xs font-semibold">
                                {transaction.company}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                isCredit ? 'bg-green-50 text-accent-success' : 'bg-red-50 text-accent-danger'
                              }`}>
                                {isCredit ? 'CREDIT' : 'DEBIT'}
                              </span>
                            </TableCell>
                            <TableCell className="text-slate-700">
                              {transaction.description || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={`font-mono font-semibold ${
                                isCredit ? 'text-accent-success' : 'text-accent-danger'
                              }`}>
                                {isCredit ? '+' : '-'}{formatCurrency(transaction.amount)}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {loading && (
        <div className="space-y-4">
          <div className="h-24 bg-slate-200 rounded-sm skeleton" />
          <div className="h-64 bg-slate-200 rounded-sm skeleton" />
        </div>
      )}
    </div>
  );
};

// Settings Component
const SettingsView = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState("");
  const [activeTab, setActiveTab] = useState("theme");
  
  // Theme colors
  const [primaryColor, setPrimaryColor] = useState("#3B82F6");
  const [headerColor, setHeaderColor] = useState("#1E293B");
  const [accentSuccess, setAccentSuccess] = useState("#10B981");
  const [accentDanger, setAccentDanger] = useState("#EF4444");

  const applyTheme = useCallback((theme) => {
    const root = document.documentElement;
    if (theme.primaryColor) root.style.setProperty('--primary-color', theme.primaryColor);
    if (theme.headerColor) root.style.setProperty('--header-color', theme.headerColor);
    if (theme.accentSuccess) root.style.setProperty('--accent-success', theme.accentSuccess);
    if (theme.accentDanger) root.style.setProperty('--accent-danger', theme.accentDanger);
  }, []);

  const fetchSettings = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/settings`)
      .then(res => {
        setSettings(res.data);
        // Load saved theme colors
        if (res.data.theme) {
          setPrimaryColor(res.data.theme.primaryColor || "#3B82F6");
          setHeaderColor(res.data.theme.headerColor || "#1E293B");
          setAccentSuccess(res.data.theme.accentSuccess || "#10B981");
          setAccentDanger(res.data.theme.accentDanger || "#EF4444");
          applyTheme(res.data.theme);
        }
      })
      .catch(() => {
        toast.error("Failed to load settings");
      })
      .finally(() => setLoading(false));
  }, [applyTheme]);

  const saveTheme = async () => {
    const theme = {
      primaryColor,
      headerColor,
      accentSuccess,
      accentDanger
    };
    
    try {
      const updatedSettings = { ...settings, theme };
      await axios.post(`${API}/settings`, updatedSettings);
      applyTheme(theme);
      toast.success("Theme saved successfully!");
    } catch (error) {
      toast.error("Failed to save theme");
    }
  };

  const resetTheme = () => {
    setPrimaryColor("#3B82F6");
    setHeaderColor("#1E293B");
    setAccentSuccess("#10B981");
    setAccentDanger("#EF4444");
    applyTheme({
      primaryColor: "#3B82F6",
      headerColor: "#1E293B",
      accentSuccess: "#10B981",
      accentDanger: "#EF4444"
    });
    toast.success("Theme reset to default");
  };

  // Preset themes
  const presetThemes = [
    { name: "Default Blue", primary: "#3B82F6", header: "#1E293B", success: "#10B981", danger: "#EF4444" },
    { name: "Royal Purple", primary: "#8B5CF6", header: "#1E1B4B", success: "#22C55E", danger: "#F43F5E" },
    { name: "Ocean Teal", primary: "#14B8A6", header: "#134E4A", success: "#34D399", danger: "#FB7185" },
    { name: "Sunset Orange", primary: "#F97316", header: "#431407", success: "#84CC16", danger: "#DC2626" },
    { name: "Forest Green", primary: "#22C55E", header: "#14532D", success: "#4ADE80", danger: "#F87171" },
    { name: "Rose Pink", primary: "#EC4899", header: "#500724", success: "#2DD4BF", danger: "#F43F5E" },
  ];

  const applyPresetTheme = (preset) => {
    setPrimaryColor(preset.primary);
    setHeaderColor(preset.header);
    setAccentSuccess(preset.success);
    setAccentDanger(preset.danger);
    applyTheme({
      primaryColor: preset.primary,
      headerColor: preset.header,
      accentSuccess: preset.success,
      accentDanger: preset.danger
    });
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const addItem = async (category, name) => {
    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }

    const id = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    try {
      await axios.post(`${API}/settings/add-item`, {
        category,
        item: { id, name }
      });
      toast.success("Item added successfully");
      setNewItem("");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to add item");
      // Error handled silently
    }
  };

  const removeItem = async (category, itemId) => {
    try {
      await axios.delete(`${API}/settings/remove-item?category=${category}&item_id=${itemId}`);
      toast.success("Item removed");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to remove item");
      // Error handled silently
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-slate-200 rounded-sm skeleton" />
        <div className="h-64 bg-slate-200 rounded-sm skeleton" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Settings - Custom Items
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 mb-6">
              <TabsTrigger value="theme" className="text-xs">
                <Settings className="w-4 h-4 mr-1" />
                Theme
              </TabsTrigger>
              <TabsTrigger value="transport" className="text-xs">
                <Truck className="w-4 h-4 mr-1" />
                Transport
              </TabsTrigger>
              <TabsTrigger value="coal" className="text-xs">
                <Package className="w-4 h-4 mr-1" />
                Coal
              </TabsTrigger>
              <TabsTrigger value="vehicles" className="text-xs">
                <Truck className="w-4 h-4 mr-1" />
                Vehicles
              </TabsTrigger>
              <TabsTrigger value="parties" className="text-xs">
                <User className="w-4 h-4 mr-1" />
                Parties
              </TabsTrigger>
            </TabsList>

            {/* Theme Customization */}
            <TabsContent value="theme">
              <div className="space-y-6">
                <p className="text-sm text-slate-600">अपने हिसाब से Theme customize करें</p>
                
                {/* Preset Themes */}
                <div className="space-y-3">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Ready-made Themes</Label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {presetThemes.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => applyPresetTheme(preset)}
                        className="p-3 rounded-sm border-2 border-slate-200 hover:border-slate-400 transition-all text-center"
                        style={{ borderColor: preset.primary }}
                      >
                        <div className="flex gap-1 justify-center mb-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.header }} />
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.success }} />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Custom Colors</Label>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Primary Color</Label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer border-0"
                        />
                        <Input
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="flex-1 rounded-sm font-mono text-xs"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Header Color</Label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={headerColor}
                          onChange={(e) => setHeaderColor(e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer border-0"
                        />
                        <Input
                          value={headerColor}
                          onChange={(e) => setHeaderColor(e.target.value)}
                          className="flex-1 rounded-sm font-mono text-xs"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Success (Green)</Label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={accentSuccess}
                          onChange={(e) => setAccentSuccess(e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer border-0"
                        />
                        <Input
                          value={accentSuccess}
                          onChange={(e) => setAccentSuccess(e.target.value)}
                          className="flex-1 rounded-sm font-mono text-xs"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600">Danger (Red)</Label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={accentDanger}
                          onChange={(e) => setAccentDanger(e.target.value)}
                          className="w-12 h-10 rounded cursor-pointer border-0"
                        />
                        <Input
                          value={accentDanger}
                          onChange={(e) => setAccentDanger(e.target.value)}
                          className="flex-1 rounded-sm font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="space-y-3 pt-4 border-t border-slate-200">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Preview</Label>
                  <div className="p-4 rounded-sm border border-slate-200" style={{ backgroundColor: headerColor }}>
                    <div className="flex items-center gap-4">
                      <div className="px-4 py-2 rounded-sm text-white font-semibold" style={{ backgroundColor: primaryColor }}>
                        Primary Button
                      </div>
                      <div className="px-4 py-2 rounded-sm text-white font-semibold" style={{ backgroundColor: accentSuccess }}>
                        +₹10,000
                      </div>
                      <div className="px-4 py-2 rounded-sm text-white font-semibold" style={{ backgroundColor: accentDanger }}>
                        -₹5,000
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={saveTheme}
                    className="text-white rounded-sm"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Theme
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={resetTheme}
                    className="rounded-sm"
                  >
                    Reset to Default
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Transport Expense Categories */}
            <TabsContent value="transport">
              <div className="space-y-4">
                <p className="text-sm text-slate-600">A1 Transport के लिए Expense Categories manage करें</p>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="नई Category का नाम (e.g., Maintenance)"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    className="flex-1 rounded-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('transport_expense_categories', newItem)}
                  />
                  <Button 
                    onClick={() => addItem('transport_expense_categories', newItem)}
                    className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {settings?.transport_expense_categories?.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-sm">
                      <span className="text-sm font-medium text-slate-700">{item.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeItem('transport_expense_categories', item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Coal Materials */}
            <TabsContent value="coal">
              <div className="space-y-4">
                <p className="text-sm text-slate-600">A1 Coal के लिए Material Types manage करें</p>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="नया Material (e.g., Gravel)"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    className="flex-1 rounded-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('coal_materials', newItem)}
                  />
                  <Button 
                    onClick={() => addItem('coal_materials', newItem)}
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {settings?.coal_materials?.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-sm">
                      <span className="text-sm font-medium text-slate-700">{item.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeItem('coal_materials', item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Vehicles */}
            <TabsContent value="vehicles">
              <div className="space-y-4">
                <p className="text-sm text-slate-600">Custom Vehicles add करें (Dropdown में दिखेंगे)</p>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Vehicle Number (e.g., MH 12 AB 1234)"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value.toUpperCase())}
                    className="flex-1 rounded-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('custom_vehicles', newItem)}
                  />
                  <Button 
                    onClick={() => addItem('custom_vehicles', newItem)}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {settings?.custom_vehicles?.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-sm">
                      <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Truck className="w-4 h-4 text-green-600" />
                        {item.name}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeItem('custom_vehicles', item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {(!settings?.custom_vehicles || settings.custom_vehicles.length === 0) && (
                    <p className="text-sm text-slate-400 col-span-3">No custom vehicles added yet. Vehicles from transactions will appear automatically.</p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Parties */}
            <TabsContent value="parties">
              <div className="space-y-4">
                <p className="text-sm text-slate-600">Custom Party Names add करें</p>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Party Name (e.g., ABC Traders)"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    className="flex-1 rounded-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('custom_parties', newItem)}
                  />
                  <Button 
                    onClick={() => addItem('custom_parties', newItem)}
                    className="bg-purple-500 hover:bg-purple-600 text-white rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {settings?.custom_parties?.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-sm">
                      <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <User className="w-4 h-4 text-purple-600" />
                        {item.name}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeItem('custom_parties', item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {(!settings?.custom_parties || settings.custom_parties.length === 0) && (
                    <p className="text-sm text-slate-400 col-span-3">No custom parties added yet. Party names from transactions will appear automatically.</p>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// Main App Component
function App() {
  const [activeCompany, setActiveCompany] = useState("AR");
  const [activeView, setActiveView] = useState("dashboard");
  const [summaries, setSummaries] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch data when company changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          axios.get(`${API}/summaries`),
          axios.get(`${API}/transactions`, { params: { company: activeCompany } }),
          axios.get(`${API}/categories`)
        ];
        
        const responses = await Promise.all(requests);
        
        setSummaries(responses[0].data);
        setTransactions(responses[1].data);
        setCategories(responses[2].data);
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [activeCompany]);

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => console.log('SW registered'))
        .catch(error => console.log('SW registration failed'));
    }
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/summaries`),
        axios.get(`${API}/transactions`, { params: { company: activeCompany } }),
        axios.get(`${API}/categories`)
      ];
      
      const responses = await Promise.all(requests);
      
      setSummaries(responses[0].data);
      setTransactions(responses[1].data);
      setCategories(responses[2].data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      await axios.delete(`${API}/transactions/${transactionId}`);
      toast.success("Transaction deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const currentSummary = summaries.find(s => s.company === activeCompany) || {
    total_credit: 0,
    total_debit: 0,
    balance: 0,
    transaction_count: 0
  };

  const totalBalance = summaries.reduce((acc, s) => acc + s.balance, 0);

  return (
    <div className="min-h-screen bg-slate-50 noise-bg">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-display font-bold text-lg tracking-tight text-slate-900">Hisab</h1>
                <p className="text-xs text-slate-500">AR, A1 INT, A1 Coal & A1 Transport</p>
              </div>
            </div>
            
            {/* View Toggle */}
            <Tabs value={activeView} onValueChange={setActiveView} className="hidden md:block">
              <TabsList className="bg-slate-100 rounded-sm">
                <TabsTrigger 
                  value="dashboard" 
                  data-testid="view-toggle-dashboard"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  <span className="font-display font-semibold">Dashboard</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="statement" 
                  data-testid="view-toggle-statement"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  <span className="font-display font-semibold">Statement</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="settings" 
                  data-testid="view-toggle-settings"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  <span className="font-display font-semibold">Settings</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="invoice" 
                  data-testid="view-toggle-invoice"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  <span className="font-display font-semibold">Invoice</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="ledger" 
                  data-testid="view-toggle-ledger"
                  className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  <span className="font-display font-semibold">Ledger</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-3">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-primary text-white rounded-sm uppercase tracking-wide font-semibold text-xs hover:bg-primary/90 btn-active"
                    data-testid="add-transaction-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Transaction
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle className="font-display font-bold">New Transaction</DialogTitle>
                    <DialogDescription>
                      Add a credit or debit transaction for <span className="font-semibold">{activeCompany}</span> company
                    </DialogDescription>
                  </DialogHeader>
                  <TransactionForm 
                    company={activeCompany}
                    categories={categories}
                  onSuccess={fetchData}
                  onClose={() => setDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile View Toggle */}
      <div className="md:hidden px-4 py-3 bg-white border-b border-slate-200">
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="w-full bg-slate-100 rounded-sm">
            <TabsTrigger 
              value="dashboard" 
              data-testid="view-toggle-dashboard-mobile"
              className="flex-1 rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger 
              value="statement" 
              data-testid="view-toggle-statement-mobile"
              className="flex-1 rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <FileText className="w-4 h-4 mr-2" />
              Statement
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              data-testid="view-toggle-settings-mobile"
              className="flex-1 rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === "statement" ? (
          <StatementView />
        ) : activeView === "settings" ? (
          <SettingsView />
        ) : activeView === "invoice" ? (
          <InvoiceView />
        ) : activeView === "ledger" ? (
          <CoalLedgerView />
        ) : activeView === "fuel" ? (
          <FuelVendorSummary />
        ) : (
          <>
            {/* Company Toggle for Dashboard */}
            <div className="mb-6">
              <Tabs value={activeCompany} onValueChange={setActiveCompany}>
                <TabsList className="bg-slate-100 rounded-sm">
                  <TabsTrigger 
                    value="AR" 
                    data-testid="company-toggle-ar"
                    className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                  >
                    <span className="font-display font-semibold">AR</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="A1 INT" 
                    data-testid="company-toggle-a1int"
                    className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                  >
                    <span className="font-display font-semibold">A1 INT</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="A1 Coal" 
                    data-testid="company-toggle-a1coal"
                    className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                  >
                    <span className="font-display font-semibold">A1 Coal</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="A1 Transport" 
                    data-testid="company-toggle-a1transport"
                    className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm px-4"
                  >
                    <span className="font-display font-semibold">A1 Transport</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {loading ? (
              <div className="space-y-4">
                <div className="bento-grid">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="h-32 bg-slate-200 rounded-sm skeleton" />
                  ))}
                </div>
                <div className="h-64 bg-slate-200 rounded-sm skeleton" />
              </div>
            ) : (
              <div className="space-y-8">
                {/* Total Balance Banner */}
                <Card className="bg-slate-900 text-white border-0 rounded-sm" data-testid="total-balance-card">
                  <CardContent className="p-6">
                    <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-400 mb-1">
                      Combined Balance (All Companies)
                    </p>
                    <p className={`font-mono text-4xl font-bold tracking-tight ${
                      totalBalance >= 0 ? 'text-accent-success' : 'text-accent-danger'
                    }`}>
                      {formatCurrency(totalBalance)}
                    </p>
                  </CardContent>
                </Card>

                {/* Company Summary Cards */}
                <div>
                  <h2 className="font-display font-bold text-xl tracking-tight text-slate-900 mb-4">
                    {activeCompany} Company Overview
                  </h2>
                  <div className="bento-grid">
                    <div className="hero-balance">
                      <BalanceCard 
                        title="Current Balance"
                        amount={currentSummary.balance}
                        icon={Wallet}
                        overline={`${activeCompany} Company`}
                      />
                    </div>
                    <BalanceCard 
                      title="Total Credit"
                      amount={currentSummary.total_credit}
                      icon={TrendingUp}
                      variant="credit"
                    />
                    <BalanceCard 
                      title="Total Debit"
                      amount={currentSummary.total_debit}
                      icon={TrendingDown}
                      variant="debit"
                    />
                  </div>
                </div>

                {/* Transactions Table */}
                <Card className="border border-slate-200 shadow-sm rounded-sm">
                  <CardHeader className="border-b border-slate-100 pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-display font-bold text-lg tracking-tight">
                        Recent Transactions
                      </CardTitle>
                      <span className="text-xs text-slate-500 font-mono">
                        {currentSummary.transaction_count} total
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <TransactionList 
                      transactions={transactions}
                      onDelete={handleDeleteTransaction}
                      onEdit={fetchData}
                      showCoalDetails={activeCompany === "A1 Coal"}
                      showTransportDetails={activeCompany === "A1 Transport"}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-slate-500">
            Dual Ledger - Financial Management for AR, A1 INT, A1 Coal & A1 Transport
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
